import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons'

import { fromJS, Map } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { getMinTermRate } from '../../../actions/companies';
import { getRoute, loading } from '../../../actions/sessions';
import { asyncActions } from '../../../actions/general';
import { getShoppingCart, updateShoppingCart } from '../../../actions/shopping_carts';
import ShoppingCartRecord from '../../../records/shopping_cart';
import { daysAsMonths, formatCurrency, formatPercent } from '../../../lib/formatters';
import { emitEvent } from '../../../lib/modal';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useSessionEntity from '../../../hooks/useSessionEntity';
import Term from '../../shared/Term';
import Error from '../../shared/Error';
import Loading from '../../shared/Loading';
import Heap from '../../../lib/heap';

const heap = new Heap();

export default function Terms({ location }) {

  const dispatch = useDispatch();
  const [isRouting, setIsRouting] = useState(false);

  const isLoading = useSelector(state => state.sessions.get('loading'));
  const session = useSelector(state => state.sessions.get('session'));
  const application = useSessionEntity('application');
  const cart = useSessionEntity('shopping_cart');
  const merchant = useSessionEntity('merchant');
  const company = useSessionEntity('company');
  const user = useSessionEntity('user');

  const [selectedTerm, setSelectedTerm] = useState({ term: null, term_period: null, payment: null });
  const [largeSinglePayment, setLargeSinglePayment] = useState({ show: false, confirmed: false });
  const [minRate, setMinRate] = useState();

  const setInitialTerm = cart => {
    if (!cart.approved_terms || selectedTerm.term) return Promise.reject('Invalid state');

    if (cart.approved_terms.count() > 0) {
      emitEvent(user, {
        path: location.pathname,
        approved_terms: cart.approved_terms
      });
    }

    const firstAvailable = cart.approved_terms.find(t => {
      return (t.get('term') == cart.num_payments && t.get('available')) || (!cart.num_payments && t.get('available'))
    });

    if (Map.isMap(firstAvailable)) {
      return Promise.resolve(setSelectedTerm({
        term: firstAvailable.get('term'),
        payment: firstAvailable.get('monthly_payment'),
        term_period: firstAvailable.get('term_period')
      }));
    } else {
      return Promise.reject('No valid terms');
    }
  }

  useRouteStatus(location);

  useEffect(() => {
    if (user) heap.identify(user.email);
  }, [user]);

  const save = e => {
    e.preventDefault();

    // Confirm for large single payment, then trigger the API call.
    if (selectedTerm.term === 1 &&
      parseFloat(cart.grand_total) > parseFloat('2000.00') &&
      !largeSinglePayment.confirmed
    ) {
      setLargeSinglePayment({ show: true, confirmed: false });
    } else {
      setLargeSinglePayment({ show: false, confirmed: true });
    }
  };

  useEffect(() => {
    if (company && !minRate) {
      dispatch(loading(true));
      dispatch(asyncActions(getMinTermRate(company.id)))
        .then(res => setMinRate(res.attributes.data))
        .then(() => {
          // get approved terms for states:
          //
          // 1) on no application;
          //    this means we're a returning borrower placing an order
          //
          // 2) on cart having no terms;
          //    this means a credit refresh just occured
          if (!application || cart.approved_terms.count() === 0) {
            dispatch(getShoppingCart(cart.id));
          }
        })
        .then(() => dispatch(loading(false)));
    }
  }, [company]);

  useEffect(() => {
    // Set initial selected term. It can be any term row if some are
    // unavailable or a previsouly selected one.
    dispatch(loading(true));

    setInitialTerm(cart)
      .then(() => dispatch(loading(false)))
      .catch(() => dispatch(loading(false)));
  }, [cart]);

  useEffect(() => {
    if (largeSinglePayment.confirmed) {
      setIsRouting(true);
      dispatch(loading(true));
      dispatch(asyncActions(
        updateShoppingCart(new ShoppingCartRecord(fromJS({
          ...cart.toJS(),
          num_payments: selectedTerm.term,
          payment_amount: selectedTerm.payment,
          term_period: selectedTerm.term_period
        }))),
        getRoute()
      ))
        .then(() => {
          dispatch(loading(false));
          setIsRouting(false);
        });
    }
  }, [largeSinglePayment]);

  const pi4Term = cart.approved_terms
    .filter(i => i.get('available') && i.get('term_period') === 'biweekly')
    .map((d, idx) => (
      <Term
        key={idx}
        term={d}
        setParentTerm={setSelectedTerm}
        parentSelectedTerm={selectedTerm}
        cart={cart}
      />
    ));

  const availableTerms = cart.approved_terms
    .filter(i => i.get('available') && i.get('term_period') === 'monthly')
    .sortBy(t => t.get('term'))
    .map((d, idx) => (
      <Term
        key={idx}
        term={d}
        setParentTerm={setSelectedTerm}
        parentSelectedTerm={selectedTerm}
        cart={cart}
      />
    ));

  const unavailableTerms = cart.approved_terms
    .filter(i => !i.get('available') && i.get('term_period') === 'monthly')
    .sortBy(t => t.get('term'))
    .map((d, idx) => (
      <Term
        key={idx}
        term={d}
        setParentTerm={setSelectedTerm}
        parentSelectedTerm={selectedTerm}
        cart={cart}
      />
    ));

  const headerSection = () => {
    if (!cart) return (<Loading active={true} />);
    
    if (cart.using_virtual_card) {
      return (
        <div className="is-size-4 has-text-centered has-text-weight-bold">
          Choose your terms<br />for your virtual card purchase:
        </div>
      )
    } else {
      return (<>
        {cart.status === 'approved' && (!session.hasApplication() || session.hasApplication() && application.status === 'approved') ?
          <div className="is-size-2 text-green-highlight is-spaced has-text-centered has-text-weight-bold">
            <FontAwesomeIcon icon={faCheck} />
            APPROVED
          </div>
          : null}

        {(merchant.supports_pi4 && pi4Term) && <div className="is-size-4 has-text-centered has-text-weight-bold">
          How do you want to pay<br/>
          for your {formatCurrency(cart.grand_total)} purchase?
        </div>}

        {!merchant.supports_pi4 && <>
          {merchant.active_promo.get('max_net_payment' > 30) ?
            <div className="is-size-4 has-text-centered has-text-weight-bold">
              No payments for {daysAsMonths(merchant.active_promo.get('max_net_payment'))}
            </div>
            : null}

          <div className="is-size-4 has-text-centered has-text-weight-bold">
            0% for {daysAsMonths(merchant.active_promo.get('max_net_duration'))},
            up to {formatCurrency(company.credit_line.get('amount'))}
          </div>

          <p>
            If you choose to go beyond {daysAsMonths(merchant.active_promo.get('max_net_duration'))}, you'll pay as little as<br />
            <strong>{formatPercent(minRate)} of the purchase price monthly. No early payoff fees</strong>.
          </p>
        </>}
      </>)
    }
  }

  if (!company || !merchant || !cart) {
    return (<Loading active={true} />);
  }

  return (
    <div id="content">
      <div className="columns is-mobile is-centered">
        <div className="column has-text-centered">
          <Error />

          {headerSection()}
        </div>
      </div>

      {merchant.supports_pi4 && pi4Term}
      {unavailableTerms}
      {availableTerms} 

      {largeSinglePayment &&
        <div className={classNames("modal", { 'is-active': largeSinglePayment.show })}>
          <div className="modal-background"></div>
          <div className="modal-content">
            <div className="box">
              <h1 className="title has-text-centered">Heads up!</h1>
              <p className="mb-5 has-text-weight-semibold">
                Did you intend to pay for your entire {formatCurrency(parseFloat(cart.grand_total)) + ' '}
                order in ONE PAYMENT, due in {daysAsMonths(merchant.active_promo.get('max_net_payment') + 1)} after your order ships?
                If not, you can go back and select a longer term.
              </p>

              <div className="field is-grouped is-grouped-centered">
                <p className="control">
                  <a
                    className="button has-text-weight-bold is-light"
                    onClick={() => setLargeSinglePayment({ show: false, confirmed: false })}>
                    Go Back
                  </a>
                </p>
                <p className="control">
                  <a
                    className="button has-text-weight-bold is-primary"
                    onClick={() => setLargeSinglePayment({ show: false, confirmed: true })}>
                    CONFIRM
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>}

      <button
        style={{ marginTop: '2.25rem' }}
        onClick={save}
        type="submit"
        role="submit"
        disabled={isLoading || isRouting}
        className={classNames('button is-primary is-large is-fullwidth has-text-weight-bold', {
          'is-loading': isLoading || isRouting
        })}>
        Continue
      </button>

    </div>
  )
}
